






































































































































































import { Component, Vue } from 'vue-property-decorator'
import Empty from '@/components/Empty/index.vue'
import {
  listMpRoles,
  listMpPermissions,
  createMpRole,
  updateMpRole,
  deleteMpRole
} from '@/api/mpAuth'
import {
  buildPermissionTree,
  MpPermission
} from '@/utils/mpPermissionHelper'

const SYSTEM_ROLE_CODES = ['ADMIN', 'USER']

@Component({
  name: 'MpRole',
  components: { Empty }
})
export default class extends Vue {
  private loading = false
  private saving = false
  private dialogVisible = false
  private dialogMode: 'add' | 'edit' | 'view' = 'add'
  private roleList: any[] = []
  private permissionList: MpPermission[] = []
  private treeProps = { label: 'label', children: 'children' }
  private editForm = {
    id: null as number | null,
    roleCode: '',
    roleName: '',
    description: '',
    status: 1,
    userCount: 0,
    boundUserNames: [] as string[]
  }

  get permissionTreeData() {
    return buildPermissionTree(this.permissionList, this.dialogMode === 'view')
  }

  get dialogTitle() {
    if (this.dialogMode === 'add') return '新增角色'
    if (this.dialogMode === 'view') return '查看角色'
    return '编辑角色'
  }

  get formRules() {
    return {
      roleName: [{ required: true, message: '请输入角色名称', trigger: 'blur' }]
    }
  }

  created() {
    this.loadData()
  }

  private isSystemRole(row: any) {
    return SYSTEM_ROLE_CODES.indexOf(row.roleCode) >= 0
  }

  private formatBoundUsers(row: any) {
    const names = row.boundUserNames || []
    if (!names.length) {
      return `共 ${row.userCount || 0} 人`
    }
    const preview = names.join('、')
    if ((row.userCount || 0) > names.length) {
      return `${preview} 等 ${row.userCount} 人`
    }
    return preview
  }

  private async loadData() {
    this.loading = true
    try {
      const [roles, permissions] = await Promise.all([
        listMpRoles(),
        listMpPermissions()
      ])
      this.roleList = (roles as any[]) || []
      this.permissionList = (permissions as MpPermission[]) || []
    } catch (e) {
      // unwrap 已提示
    } finally {
      this.loading = false
    }
  }

  private resetForm() {
    this.editForm = {
      id: null,
      roleCode: '',
      roleName: '',
      description: '',
      status: 1,
      userCount: 0,
      boundUserNames: []
    }
    this.syncTreeCheckedKeys([])
  }

  private onDialogOpened() {
    if (this.dialogMode === 'add') {
      this.syncTreeCheckedKeys([])
    }
  }

  private syncTreeCheckedKeys(ids: number[] = []) {
    this.$nextTick(() => {
      const tree = this.$refs.permTree as any
      if (tree) {
        tree.setCheckedKeys((ids || []).map(Number))
      }
    })
  }

  private resetDialog() {
    this.resetForm()
    const form = this.$refs.roleForm as any
    if (form) form.clearValidate()
  }

  private initPermFromRole(role: any) {
    this.syncTreeCheckedKeys(role.permissionIds || [])
  }

  private fillRoleDetail(row: any) {
    this.editForm = {
      id: row.id,
      roleCode: row.roleCode || '',
      roleName: row.roleName || '',
      description: row.description || '',
      status: row.status != null ? row.status : 1,
      userCount: row.userCount || 0,
      boundUserNames: row.boundUserNames || []
    }
    this.initPermFromRole(row)
  }

  private openAdd() {
    this.dialogMode = 'add'
    this.resetForm()
    this.dialogVisible = true
  }

  private openView(row: any) {
    this.dialogMode = 'view'
    this.dialogVisible = true
    this.fillRoleDetail(row)
  }

  private openEdit(row: any) {
    this.dialogMode = 'edit'
    this.dialogVisible = true
    this.fillRoleDetail(row)
  }

  private collectPermissionIds(): number[] {
    const tree = this.$refs.permTree as any
    if (!tree) return []
    return (tree.getCheckedKeys() || []).map(Number)
  }

  private saveRole() {
    const form = this.$refs.roleForm as any
    form.validate(async (valid: boolean) => {
      if (!valid) return
      this.saving = true
      try {
        const permissionIds = this.collectPermissionIds()
        if (this.dialogMode === 'add') {
          await createMpRole({
            roleName: this.editForm.roleName.trim(),
            description: this.editForm.description,
            status: this.editForm.status,
            permissionIds
          })
        } else {
          await updateMpRole({
            id: this.editForm.id as number,
            roleName: this.editForm.roleName.trim(),
            description: this.editForm.description,
            status: this.editForm.status,
            permissionIds
          })
        }
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.loadData()
      } catch (e: any) {
        this.$message.error(e.message || '保存失败')
      } finally {
        this.saving = false
      }
    })
  }

  private handleDelete(row: any) {
    if (this.isSystemRole(row)) return
    if ((row.userCount || 0) > 0) {
      this.$message.warning(`该角色仍有 ${row.userCount} 个用户，无法删除`)
      return
    }
    this.$confirm(`确认删除角色「${row.roleName}」？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      try {
        await deleteMpRole(row.id)
        this.$message.success('删除成功')
        this.loadData()
      } catch (e: any) {
        this.$message.error(e.message || '删除失败')
      }
    }).catch(() => {})
  }

  private toggleStatus(row: any) {
    const nextStatus = String(row.status) === '0' ? 1 : 0
    const actionText = nextStatus === 1 ? '启用' : '禁用'
    const userCount = row.userCount || 0
    let message = `确认${actionText}角色「${row.roleName}」？`
    if (nextStatus === 0 && userCount > 0) {
      message = `该角色仍有 ${userCount} 个用户在使用，禁用后这些用户将受影响。确认禁用？`
    }
    this.$confirm(message, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    }).then(async () => {
      try {
        await updateMpRole({
          id: row.id,
          roleName: row.roleName,
          description: row.description,
          status: nextStatus,
          permissionIds: row.permissionIds || []
        })
        this.$message.success(`${actionText}成功`)
        this.loadData()
      } catch (e: any) {
        this.$message.error(e.message || `${actionText}失败`)
      }
    }).catch(() => {})
  }
}
